<template>
  <div class="dashboard-page">
    <!-- Search bar -->
    <div class="search-section card">
      <div class="search-bar touch-feedback" @click="$router.push('/stall/products')">
        <span class="search-icon">🔍</span>
        <input 
          type="text" 
          v-model="searchQuery" 
          :placeholder="$t('Dashboard.line10_placeholder')"
          @keypress.enter="handleSearch"
        />
        <button class="search-btn" @click="handleSearch">{{ $t('common.search') }}</button>
      </div>
    </div>

    <!-- Stats grid -->
    <div class="stats-grid">
      <div class="stat-card touch-feedback" @click="$router.push('/stall/products')">
        <div class="stat-icon stat-icon-blue">
          <span>📦</span>
        </div>
        <div class="stat-info">
          <div class="stat-label">{{ $t('Dashboard.line24_text') }}</div>
          <div class="stat-value">{{ formatMoney(stats.monthPurchaseAmount) }}</div>
          <div class="stat-trend positive">
            <span class="trend-icon">⬆️</span>
            <span>{{ stats.monthTrend }}% vs 上月</span>
          </div>
        </div>
      </div>

      <div class="stat-card touch-feedback" @click="$router.push('/stall/products')">
        <div class="stat-icon stat-icon-green">
          <span>✅</span>
        </div>
        <div class="stat-info">
          <div class="stat-label">{{ $t('Dashboard.line38_text') }}</div>
          <div class="stat-value">{{ stats.pendingOrders }}</div>
          <div class="stat-subtitle">{{ $t('Dashboard.line40_text') }}</div>
        </div>
      </div>

      <div class="stat-card touch-feedback" @click="$router.push('/payment')">
        <div class="stat-icon stat-icon-orange">
          <span>💳</span>
        </div>
        <div class="stat-info">
          <div class="stat-label">{{ $t('Dashboard.line49_text') }}</div>
          <div class="stat-value">{{ formatMoney(stats.balance) }}</div>
          <div class="stat-subtitle">待支付：{{ formatMoney(stats.unpaidAmount) }}</div>
        </div>
      </div>

      <div class="stat-card touch-feedback" @click="$router.push('/seller')">
        <div class="stat-icon stat-icon-purple">
          <span>👥</span>
        </div>
        <div class="stat-info">
          <div class="stat-label">{{ $t('Dashboard.line60_text') }}</div>
          <div class="stat-value">{{ stats.sellerCount }}</div>
          <div class="stat-subtitle">{{ $t('Dashboard.line62_text') }}</div>
        </div>
      </div>
    </div>

    <!-- Quick actions -->
    <div class="quick-actions-section card">
      <div class="section-header">
        <h3 class="section-title">{{ $t('Dashboard.line70_text') }}</h3>
      </div>
      <div class="quick-actions-grid">
        <div class="action-item touch-feedback" @click="$router.push('/shopping-cart')">
          <div class="action-icon action-icon-blue">
            <span>🛒</span>
          </div>
          <div class="action-label">{{ $t('Dashboard.line77_text') }}</div>
          <span class="action-badge" v-if="stats.cartCount > 0">{{ stats.cartCount }}件</span>
        </div>

        <div class="action-item touch-feedback" @click="$router.push('/stall/products')">
          <div class="action-icon action-icon-green">
            <span>➕</span>
          </div>
          <div class="action-label">{{ $t('Dashboard.line85_text') }}</div>
        </div>

        <div class="action-item touch-feedback" @click="$router.push('/payment')">
          <div class="action-icon action-icon-orange">
            <span>💰</span>
          </div>
          <div class="action-label">{{ $t('Dashboard.line92_text') }}</div>
          <span class="action-badge action-badge-red" v-if="stats.unpaidAmount > 0">{{ stats.unpaidAmount }}元</span>
        </div>

        <div class="action-item touch-feedback" @click="$router.push('/quote/generate')">
          <div class="action-icon action-icon-purple">
            <span>📝</span>
          </div>
          <div class="action-label">{{ $t('Dashboard.line100_text') }}</div>
        </div>

        <div class="action-item touch-feedback" @click="$router.push('/stall/dashboard')">
          <div class="action-icon action-icon-pink">
            <span>🏪</span>
          </div>
          <div class="action-label">{{ $t('Dashboard.line107_text') }}</div>
        </div>

        <div class="action-item touch-feedback" @click="$router.push('/delivery')">
          <div class="action-icon action-icon-cyan">
            <span>🚚</span>
          </div>
          <div class="action-label">{{ $t('Dashboard.line114_text') }}</div>
        </div>

        <div class="action-item touch-feedback" @click="$router.push('/stall/inventory')">
          <div class="action-icon action-icon-teal">
            <span>📊</span>
          </div>
          <div class="action-label">{{ $t('Dashboard.line121_text') }}</div>
        </div>

        <div class="action-item touch-feedback" @click="$router.push('/message')">
          <div class="action-icon action-icon-indigo">
            <span>💬</span>
          </div>
          <div class="action-label">{{ $t('Dashboard.line128_text') }}</div>
          <span class="action-badge action-badge-red" v-if="stats.unreadMessages > 0">{{ stats.unreadMessages }}</span>
        </div>
      </div>
    </div>

    <!-- Charts section (visible on larger screens) -->
    <div class="charts-container" v-if="showCharts">
      <div class="chart-card card">
        <div class="section-header">
          <h3 class="section-title">{{ $t('Dashboard.line138_text') }}</h3>
          <div class="chart-tabs">
            <button 
              v-for="(label, key) in periodLabels" 
              :key="key"
              :class="['tab-btn', { active: selectedPeriod === key }]"
              @click="selectedPeriod = key"
            >
              {{ label }}
            </button>
          </div>
        </div>
        <div class="chart-wrapper">
          <canvas ref="purchaseChart"></canvas>
        </div>
      </div>

      <div class="chart-card card mt-2">
        <div class="section-header">
          <h3 class="section-title">{{ $t('Dashboard.line157_text') }}</h3>
        </div>
        <div class="chart-wrapper">
          <canvas ref="distributionChart"></canvas>
        </div>
      </div>
    </div>

    <!-- Recent orders -->
    <div class="recent-orders-section card">
      <div class="section-header">
        <h3 class="section-title">{{ $t('Dashboard.line168_text') }}</h3>
        <router-link to="/stall/products" class="view-all">{{ $t('Dashboard.line169_text') }}</router-link>
      </div>
      
      <div class="orders-list">
        <div 
          v-for="order in recentOrders" 
          :key="order.id"
          class="order-item touch-feedback"
          @click="$router.push(`/stall/products/${order.id}`)"
        >
          <div class="order-left">
            <div class="order-no">{{ order.purchaseOrderNo }}</div>
            <div class="order-seller">商家：{{ order.sellerShopName }}</div>
          </div>
          <div class="order-right">
            <div class="order-amount">{{ formatMoney(order.totalAmount) }}</div>
            <span :class="['order-status', `status-${order.orderStatus}`]">
              {{ getOrderStatusLabel(order.orderStatus) }}
            </span>
          </div>
        </div>

        <div v-if="recentOrders.length === 0" class="empty-state">
          <div class="empty-icon">📦</div>
          <p>{{ $t('Dashboard.line193_text') }}</p>
          <router-link to="/stall/products" class="btn btn-primary btn-small">
            创建第一个订单
          </router-link>
        </div>
      </div>
    </div>

    <!-- System notice -->
    <div class="notice-section card" v-if="systemNotice">
      <div class="notice-content touch-feedback" @click="showFullNotice">
        <span class="notice-icon">ℹ️</span>
        <span class="notice-text">{{ systemNotice }}</span>
        <span class="notice-arrow">›</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Chart from 'chart.js';

export default {
  name: 'Dashboard',
  data() {
    return {
      searchQuery: '',
      showCharts: true,
      selectedPeriod: 'month',
      periodLabels: {
        week: '周',
        month: '月',
        quarter: '季',
        year: '年'
      },
      stats: {
        monthPurchaseAmount: '1234567',
        monthTrend: '+12.5%',
        pendingOrders: 5,
        balance: '50000',
        sellerCount: 18,
        cartCount: 3,
        unpaidAmount: '45000',
        unreadMessages: 2
      },
      recentOrders: [
        { id: 1, purchaseOrderNo: 'PO20240304001', sellerShopName: '宏图批发商行', totalAmount: 15680, orderStatus: 2 },
        { id: 2, purchaseOrderNo: 'PO20240303002', sellerShopName: '诚信建材店', totalAmount: 8920, orderStatus: 3 },
        { id: 3, purchaseOrderNo: 'PO20240302003', sellerShopName: '优品供应链', totalAmount: 23450, orderStatus: 1 },
        { id: 4, purchaseOrderNo: 'PO20240301004', sellerShopName: '辉煌贸易公司', totalAmount: 5670, orderStatus: 5 }
      ],
      systemNotice: '系统将于今晚 23:00 进行升级维护，请提前保存数据。',
      purchaseChart: null,
      distributionChart: null,
      orderStatusMap: {
        0: '草稿',
        1: '已提交',
        2: '已确认',
        3: '已发货',
        4: '已收货',
        5: '已完成',
        '-1': '已取消'
      }
    };
  },
  computed: {
    ...mapGetters('auth', ['userName'])
  },
  methods: {
    formatMoney(value) {
      if (typeof value === 'number') {
        return '¥' + value.toLocaleString('zh-CN');
      }
      return '¥' + value.replace(/,/g, '');
    },

    getOrderStatusLabel(status) {
      return this.orderStatusMap[status] || '未知状态';
    },

    handleSearch() {
      if (this.searchQuery.trim()) {
        this.showToast(`搜索"${this.searchQuery}"`, 'info');
        this.searchQuery = '';
      }
    },

    showToast(message, type = 'info') {
      // Simple toast implementation
      const toast = document.createElement('div');
      toast.className = `toast toast-${type}`;
      toast.textContent = message;
      document.body.appendChild(toast);
      
      setTimeout(() => {
        toast.classList.add('show');
      }, 10);

      setTimeout(() => {
        toast.classList.remove('show');
        setTimeout(() => toast.remove(), 300);
      }, 3000);
    },

    showFullNotice() {
      this.showToast('查看完整公告功能开发中...', 'info');
    },

    initCharts() {
      // Destroy existing charts
      if (this.purchaseChart) this.purchaseChart.destroy();
      if (this.distributionChart) this.distributionChart.destroy();

      // Purchase trend chart (line)
      const purchaseCtx = this.$refs.purchaseChart?.getContext('2d');
      if (purchaseCtx) {
        this.purchaseChart = new Chart(purchaseCtx, {
          type: 'line',
          data: {
            labels: ['周一', '周二', '周三', '周四', '周五', '周六', '周日'],
            datasets: [{
              label: '采购金额',
              data: [12000, 19000, 15000, 25000, 22000, 30000, 28000],
              borderColor: '#3498db',
              backgroundColor: 'rgba(52, 152, 219, 0.1)',
              borderWidth: 2,
              fill: true,
              tension: 0.4,
              pointBackgroundColor: '#3498db',
              pointBorderColor: '#fff',
              pointBorderWidth: 2,
              pointRadius: 4
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                mode: 'index',
                intersect: false,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleFont: { size: 12 },
                bodyFont: { size: 12 }
              }
            },
            scales: {
              x: {
                grid: { display: false },
                ticks: { font: { size: 10 } }
              },
              y: {
                beginAtZero: true,
                grid: { color: '#f0f0f0' },
                ticks: {
                  callback: (value) => '¥' + value / 1000 + 'k',
                  font: { size: 10 }
                }
              }
            }
          }
        });
      }

      // Distribution chart (doughnut)
      const distCtx = this.$refs.distributionChart?.getContext('2d');
      if (distCtx) {
        this.distributionChart = new Chart(distCtx, {
          type: 'doughnut',
          data: {
            labels: ['已完成', '处理中', '待支付', '已取消'],
            datasets: [{
              data: [45, 25, 20, 10],
              backgroundColor: ['#2ecc71', '#3498db', '#f39c12', '#e74c3c'],
              borderWidth: 0,
              hoverOffset: 10
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom',
                labels: {
                  padding: 15,
                  font: { size: 11 },
                  usePointStyle: true
                }
              },
              tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleFont: { size: 12 },
                bodyFont: { size: 12 }
              }
            }
          }
        });
      }
    }
  },
  mounted() {
    // Check if already logged in
    if (!localStorage.getItem('token') && !sessionStorage.getItem('token')) {
      this.$router.push('/login');
      return;
    }

    // Initialize charts with a delay for proper rendering
    setTimeout(() => {
      this.initCharts();
    }, 100);

    // Listen for resize events
    window.addEventListener('resize', () => {
      this.showCharts = window.innerWidth > 360;
    });
  },
  beforeUnmount() {
    if (this.purchaseChart) this.purchaseChart.destroy();
    if (this.distributionChart) this.distributionChart.destroy();
  }
};
</script>

<style lang="scss" scoped>
.dashboard-page {
  padding-bottom: 80px;
}

// Search section
.search-section {
  margin-bottom: 16px;
  
  .search-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    background: white;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    
    .search-icon {
      font-size: 18px;
    }
    
    input {
      flex: 1;
      border: none;
      outline: none;
      font-size: 14px;
      background: transparent;
      
      &::placeholder {
        color: #999;
      }
    }
    
    .search-btn {
      padding: 8px 16px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border: none;
      border-radius: 6px;
      font-size: 13px;
      cursor: pointer;
      transition: opacity 0.2s ease;
      
      &:active {
        opacity: 0.8;
      }
    }
  }
}

// Stats grid
.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 14px;
  display: flex;
  gap: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:active {
    transform: scale(0.98);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  }
  
  .stat-icon {
    width: 52px;
    height: 52px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    flex-shrink: 0;
    
    &.stat-icon-blue { background: linear-gradient(135deg, #3498db, #2980b9); }
    &.stat-icon-green { background: linear-gradient(135deg, #2ecc71, #27ae60); }
    &.stat-icon-orange { background: linear-gradient(135deg, #f39c12, #d68910); }
    &.stat-icon-purple { background: linear-gradient(135deg, #9b59b6, #8e44ad); }
  }
  
  .stat-info {
    flex: 1;
    min-width: 0;
    
    .stat-label {
      font-size: 12px;
      color: #999;
      margin-bottom: 6px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    
    .stat-value {
      font-size: 18px;
      font-weight: bold;
      color: #2c3e50;
      margin-bottom: 4px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    
    .stat-subtitle {
      font-size: 11px;
      color: #999;
    }
  }
}

.stat-trend {
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &.positive {
    color: #2ecc71;
  }
  
  .trend-icon {
    font-size: 10px;
  }
}

// Quick actions
.quick-actions-section {
  margin-bottom: 16px;
  
  .section-header {
    margin-bottom: 14px;
    padding-bottom: 10px;
    border-bottom: 1px solid #f0f0f0;
    
    .section-title {
      margin: 0;
      font-size: 15px;
      font-weight: 600;
      color: #2c3e50;
    }
  }
}

.quick-actions-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.action-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 10px 6px;
  background: #f8f9fa;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  
  &:active {
    transform: scale(0.95);
    background: #e9ecef;
  }
  
  .action-icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    margin-bottom: 4px;
    
    &.action-icon-blue { background: #3498db; }
    &.action-icon-green { background: #2ecc71; }
    &.action-icon-orange { background: #f39c12; }
    &.action-icon-purple { background: #9b59b6; }
    &.action-icon-pink { background: #e91e63; }
    &.action-icon-cyan { background: #00bcd4; }
    &.action-icon-teal { background: #009688; }
    &.action-icon-indigo { background: #3f51b5; }
  }
  
  .action-label {
    font-size: 10px;
    color: #555;
    text-align: center;
    margin: 0;
  }
  
  .action-badge {
    position: absolute;
    top: -4px;
    right: -4px;
    padding: 2px 6px;
    background: #e74c3c;
    color: white;
    border-radius: 10px;
    font-size: 10px;
    font-weight: 600;
    animation: pulse 2s infinite;
    
    &.action-badge-red { background: #e74c3c; }
  }
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

// Charts container
.charts-container {
  display: grid;
  gap: 12px;
  margin-bottom: 16px;
}

.chart-card {
  .section-header {
    margin-bottom: 14px;
    padding-bottom: 10px;
    border-bottom: 1px solid #f0f0f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .section-title {
      margin: 0;
      font-size: 15px;
      font-weight: 600;
      color: #2c3e50;
    }
    
    .chart-tabs {
      display: flex;
      gap: 6px;
      
      .tab-btn {
        padding: 4px 10px;
        border: 1px solid #ddd;
        background: white;
        border-radius: 6px;
        cursor: pointer;
        font-size: 11px;
        color: #666;
        transition: all 0.2s ease;
        
        &:hover {
          border-color: #3498db;
          color: #3498db;
        }
        
        &.active {
          background: #3498db;
          color: white;
          border-color: #3498db;
        }
      }
    }
  }
  
  .chart-wrapper {
    position: relative;
    height: 200px;
  }
}

.mt-2 { margin-top: 16px; }

// Recent orders
.recent-orders-section {
  margin-bottom: 16px;
  
  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 12px;
    border-bottom: 1px solid #f0f0f0;
    
    .section-title {
      margin: 0;
      font-size: 15px;
      font-weight: 600;
      color: #2c3e50;
    }
    
    .view-all {
      font-size: 12px;
      color: #3498db;
      text-decoration: none;
      transition: color 0.2s ease;
      
      &:hover {
        color: #2980b9;
      }
    }
  }
}

.orders-list {
  .order-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 0;
    border-bottom: 1px solid #f5f5f5;
    cursor: pointer;
    
    &:last-child {
      border-bottom: none;
    }
    
    .order-left {
      flex: 1;
      
      .order-no {
        font-size: 13px;
        font-weight: 500;
        color: #2c3e50;
        margin-bottom: 4px;
      }
      
      .order-seller {
        font-size: 11px;
        color: #999;
      }
    }
    
    .order-right {
      text-align: right;
      
      .order-amount {
        font-size: 16px;
        font-weight: bold;
        color: #e74c3c;
        margin-bottom: 6px;
      }
      
      .order-status {
        display: inline-block;
        padding: 3px 10px;
        border-radius: 12px;
        font-size: 11px;
        font-weight: 500;
        
        &.status-0 { background: #f0f0f0; color: #999; }
        &.status-1 { background: #fff3cd; color: #856404; }
        &.status-2 { background: #d1ecf1; color: #0c5460; }
        &.status-3 { background: #cfe2ff; color: #084298; }
        &.status-4 { background: #d1e7dd; color: #0f5132; }
        &.status-5 { background: #d1e7dd; color: #0f5132; }
        &.status--1 { background: #f8d7da; color: #842029; }
      }
    }
  }
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #999;
  
  .empty-icon {
    font-size: 48px;
    margin-bottom: 12px;
    opacity: 0.5;
  }
  
  p {
    margin: 0 0 16px 0;
    font-size: 14px;
  }
}

.btn-small {
  padding: 8px 16px;
  font-size: 13px;
  border-radius: 6px;
  text-decoration: none;
  display: inline-block;
}

// Notice section
.notice-section {
  .notice-content {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    background: #e3f2fd;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.2s ease;
    
    &:active {
      background: #bbdefb;
    }
    
    .notice-icon {
      font-size: 16px;
    }
    
    .notice-text {
      flex: 1;
      font-size: 13px;
      color: #1976d2;
    }
    
    .notice-arrow {
      font-size: 16px;
      color: #1976d2;
    }
  }
}

// Toast notification
.toast {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%) translateY(-100px);
  padding: 12px 24px;
  border-radius: 8px;
  color: white;
  font-size: 14px;
  z-index: 1000;
  opacity: 0;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-width: 90%;
  text-align: center;
  
  &.show {
    transform: translateX(-50%) translateY(0);
    opacity: 1;
  }
  
  &.toast-success { background: #2ecc71; }
  &.toast-error { background: #e74c3c; }
  &.toast-info { background: #3498db; }
}
</style>
